import { KeyboardEvent, RefObject, useEffect, useRef, useState } from 'react';

type UseChipsArrowNavigationArgs = {
  // Container holding the Chips input and its .p-chips-token elements.
  containerRef: RefObject<HTMLDivElement | null>;
  chipsCount: number;
  disabled?: boolean;
  onRemoveChipAt: (index: number) => void;
  // Fired on every claimed key so the Ctrl+A select-all visuals drop with it.
  onTakeover?: () => void;
};

const FOCUSED_CHIP_CLASS = 'chip-arrow-focused';
const CARET_HIDDEN_CLASS = 'chips-input-caret-hidden';
const CHIPS_INPUT_SELECTOR = '.p-chips input';
const SCROLL_CONTAINER_SELECTOR = '.p-chips-multiple-container';

// Nearest-edge scroll so the chip walk never leaves the highlighted token outside the horizontal strip.
const scrollChipIntoView = (
  scrollContainer: HTMLElement,
  token: HTMLElement
): void => {
  const tokenLeft = token.offsetLeft;
  const tokenRight = tokenLeft + token.offsetWidth;
  if (tokenLeft < scrollContainer.scrollLeft) {
    scrollContainer.scrollLeft = tokenLeft;
  } else if (
    tokenRight >
    scrollContainer.scrollLeft + scrollContainer.clientWidth
  ) {
    scrollContainer.scrollLeft = tokenRight - scrollContainer.clientWidth;
  }
};

// ArrowLeft walks the chip highlight leftwards once the caret sits at the start of the input; ArrowRight walks back and past the last chip returns to the caret. Backspace/Delete removes the highlighted chip. Replaces Prime's own empty-input-only walk via capture-phase stopPropagation.
export const useChipsArrowNavigation = ({
  containerRef,
  chipsCount,
  disabled = false,
  onRemoveChipAt,
  onTakeover,
}: UseChipsArrowNavigationArgs) => {
  const [focusedChipIndex, setFocusedChipIndex] = useState<number | null>(null);
  // Last painted index — skips the idle repaint and repeated scroll snapping.
  const lastPaintedIndexRef = useRef<number | null>(null);

  // A chips change invalidates the tracked index — drop or clamp it.
  useEffect(() => {
    if (focusedChipIndex === null) return;
    if (chipsCount === 0) {
      setFocusedChipIndex(null);
      return;
    }
    if (focusedChipIndex > chipsCount - 1) {
      setFocusedChipIndex(chipsCount - 1);
    }
  }, [chipsCount, focusedChipIndex]);

  // Prime renders the tokens, so the highlight is toggled on the DOM nodes directly.
  useEffect(() => {
    const lastPaintedIndex = lastPaintedIndexRef.current;
    // Nothing highlighted now or before — nothing to paint or clean up.
    if (focusedChipIndex === null && lastPaintedIndex === null) return;
    const tokens =
      containerRef.current?.querySelectorAll<HTMLElement>('.p-chips-token');
    tokens?.forEach((token, index) =>
      token.classList.toggle(FOCUSED_CHIP_CLASS, index === focusedChipIndex)
    );
    const scrollContainer = containerRef.current?.querySelector<HTMLElement>(
      SCROLL_CONTAINER_SELECTOR
    );
    const focusedToken =
      focusedChipIndex === null ? undefined : tokens?.[focusedChipIndex];
    // Scroll only when the highlight moved — unrelated re-renders must not fight the user's manual scroll.
    if (
      scrollContainer &&
      focusedToken &&
      focusedChipIndex !== lastPaintedIndex
    ) {
      scrollChipIntoView(scrollContainer, focusedToken);
    }
    // The input keeps DOM focus so the keys keep flowing, but a blinking caret would fight the chip highlight.
    const input =
      containerRef.current?.querySelector<HTMLInputElement>(
        CHIPS_INPUT_SELECTOR
      );
    input?.classList.toggle(CARET_HIDDEN_CLASS, focusedChipIndex !== null);
    // Announce the walked chip to assistive tech via Prime's own token id.
    if (input) {
      if (focusedToken?.id) {
        input.setAttribute('aria-activedescendant', focusedToken.id);
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }
    lastPaintedIndexRef.current = focusedChipIndex;
    // No dep array on purpose: Prime can recreate the token nodes on any re-render, dropping the toggled classes.
  });

  const clearChipFocus = () => setFocusedChipIndex(null);

  // Capture phase: handled keys stop propagating, so Prime's own chip walk and remove-last never double-run.
  const onChipsNavKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled || chipsCount === 0) return;
    const input =
      containerRef.current?.querySelector<HTMLInputElement>(
        CHIPS_INPUT_SELECTOR
      );
    if (!input || event.target !== input) return;

    const hasModifier =
      event.ctrlKey || event.metaKey || event.altKey || event.shiftKey;
    const caretAtStart = input.selectionStart === 0 && input.selectionEnd === 0;

    // Prime focuses its own token listbox on ArrowLeft over an empty input regardless of modifiers — block that second walk.
    if (event.key === 'ArrowLeft' && hasModifier && input.value === '') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.key === 'ArrowLeft' && !hasModifier) {
      // Caret still inside the typed text — keep the native move.
      if (focusedChipIndex === null && !caretAtStart) return;
      event.preventDefault();
      event.stopPropagation();
      onTakeover?.();
      setFocusedChipIndex((current) =>
        current === null ? chipsCount - 1 : Math.max(0, current - 1)
      );
      return;
    }

    if (event.key === 'ArrowRight' && !hasModifier) {
      if (focusedChipIndex === null) return;
      event.preventDefault();
      event.stopPropagation();
      onTakeover?.();
      setFocusedChipIndex(
        focusedChipIndex === chipsCount - 1 ? null : focusedChipIndex + 1
      );
      return;
    }

    if (
      (event.key === 'Backspace' || event.key === 'Delete') &&
      !hasModifier &&
      focusedChipIndex !== null
    ) {
      event.preventDefault();
      event.stopPropagation();
      onTakeover?.();
      onRemoveChipAt(focusedChipIndex);
      const newCount = chipsCount - 1;
      setFocusedChipIndex(
        newCount === 0 ? null : Math.min(focusedChipIndex, newCount - 1)
      );
      return;
    }

    // Bare modifiers keep the highlight (Ctrl+A arrives as two keydowns); anything else drops it.
    if (
      focusedChipIndex !== null &&
      !['Shift', 'Control', 'Meta', 'Alt'].includes(event.key)
    ) {
      clearChipFocus();
    }
  };

  return { focusedChipIndex, onChipsNavKeyDownCapture, clearChipFocus };
};
